import * as cx from 'classnames';
import compact from 'lodash-es/compact';
import map from 'lodash-es/map';
import { NOT_ALL_MESSAGE_PHOTOS_HAVE_BEEN_UPLOADED_SEND_ANYWAY } from 'mk/autogenerated/translations/PhotoUpload.d1142413bf7bb82ec6558d54b16ef8b2'
import FieldWrapper from 'mk/forms/FieldWrapper';
import { OwnProps as PhotoPreviewOwnProps, PhotoPreview } from 'mk/forms/PhotoPreview';
import { STATUS, Uploader } from 'mk/forms/Uploader';
import { Context } from 'mk/interfaces';
import { hasBuggyPhotoUploadInput, hasBuggySelectImages } from 'mk2/helpers/detects';
import * as React from 'react';

interface OwnProps {
    context: Context;
    className?: string;
    name: string;
    errors?: string[];
    photoType: string;
    previewPhotoSize: string;
    multiple?: boolean;
    initialPhotos: any[]; // array of photo tokens
    initial?: any[];
    customPreview?: React.ComponentType<PhotoPreviewOwnProps>;

    onChange(photos: any[]);
}

interface OwnState {
    useNativeAndroidUpload: boolean;
    useGenericFileSelectDialog: boolean;
    value: any[];
}

export class PhotoUpload extends React.Component<OwnProps, OwnState> {

    public static defaultProps = {
        initial: [],
    };

    private uploader: Uploader;

    public static isFormField() {
        return true;
    }

    constructor(props, context) {
        super(props, context);

        this.state = {
            value: props.initial,
            useNativeAndroidUpload: false,
            useGenericFileSelectDialog: false,
        };
    }

    public clear() {
        if (!this.hasValue()) {
            return;
        }

        this.setState({
            value: this.props.initial,
        });

        // Notify parent about change
        if (this.props.onChange) {
            this.props.onChange(this.props.initial);
        }

        this.uploader.clear();
    }

    public hasValue() {
        return this.state.value.length > 0;
    }

    public getValue() {
        return this.state.value;
    }

    public setValue(value) {
        this.setState({ value });
    }

    public validate(form) {
        const stillUploading = [];
        const photos = map(this.state.value, (photo) => {
            if (photo.status === STATUS.SENDING || photo.status === STATUS.NEW) { stillUploading.push(photo.id); }
            if (photo.status === STATUS.DONE) { return { ...photo, orientation: photo.orientation}; }
        });
        if (stillUploading.length) {
            const errorText = NOT_ALL_MESSAGE_PHOTOS_HAVE_BEEN_UPLOADED_SEND_ANYWAY;
            if (!confirm(errorText)) { return [null, errorText]; }
            this.uploader.bulkCancel(stillUploading);
        }
        return [compact(photos), null];
    }

    public componentDidMount() {
        this.setState({
            useNativeAndroidUpload: hasBuggyPhotoUploadInput(window.navigator.userAgent),
            useGenericFileSelectDialog: hasBuggySelectImages(window.navigator.userAgent),
        });
    }

    public render() {
        const {value} = this.state;
        const PreviewComponent = this.props.customPreview || PhotoPreview;
        return (
            <FieldWrapper
                className={cx('photo-upload', this.props.className)}
                errors={this.props.errors}
                name={this.props.name}
            >
                <div className="g-form-photo-upload form-action-button">
                    <Uploader
                        context={this.props.context}
                        photoType={this.props.photoType}
                        multiple={this.props.multiple}
                        initialPhotos={this.props.initialPhotos}
                        useNativeAndroidUpload={this.state.useNativeAndroidUpload}
                        useGenericFileSelectDialog={this.state.useGenericFileSelectDialog}
                        onFilesUpdated={this.updatePhotos}
                        ref={(r) => this.uploader = r}
                    />

                    <div
                        className={cx('trigger', value.length ? 'selected' : '')}
                        onClick={this.onOpenUploaderClick}
                    >
                        <span className="camera-icon" />
                    </div>

                    {value.length > 0 && <div className="value-arrow" />}
                </div>
                {value.length > 0 && (
                    <div className="preview-panel">
                        {map(value, (photo) => {
                            return (
                                <PreviewComponent
                                    key={photo.id}
                                    context={this.props.context}
                                    item={photo}
                                    previewPhotoSize={this.props.previewPhotoSize}
                                    onCancel={this.cancelPhoto}
                                    onRotate={this.rotatePhoto}
                                />
                            );
                        })}
                    </div>
                )}
            </FieldWrapper>
        );
    }

    private onOpenUploaderClick = () => {
        this.uploader.openFileExplorer();
    };

    private updatePhotos = (photos) => {
        this.setState({value: photos});
        if (this.props.onChange) {
            this.props.onChange(photos);
        }
    };

    private cancelPhoto = (id) => {
        this.uploader.cancel(id);
    };

    private rotatePhoto = (id) => {
        this.uploader.rotate(id);
    };

}
